import {
  SimpleFacilityModel,
  CreateFacilityModel,
  UpdateFacilityTemplateModel,
  FacilityFeatureFlagsEnum,
  FacilityModel,
  FacilityStatusEnum,
} from "../utils/types/facility";
import {http} from "./api/restApiClient";
import {ApiResponse} from "apisauce";
import {Page} from "./api/pagination";

import {OrganizationsService} from "./OrganizationsService";
import {PccIntegrationStatus} from "./PccService";

export const facilityHasPccTurnedOn = (facility: Facility) =>
  facility?.pccIntegrationStatus === PccIntegrationStatus.PccTurnedOn;

export const facilityHasPccUploadActive = (facility: Facility) =>
  Boolean(facility?.pccUploadActive);

export const getFacilityFeatureFlags = (facility: Facility) => ({
  facilityHasPccTurnedOn: facilityHasPccTurnedOn(facility),
  facilityHasPccUploadActive: facilityHasPccUploadActive(facility),
  facilityHasOrgAdminOnlyDeleteAdmissions:
    !!facility.featureFlags?.[
      FacilityFeatureFlagsEnum.ORG_ADMIN_ONLY_DELETE_ADMISSIONS
    ],
  facilityHasFacilityAdminOnlyDeleteAdmissions:
    !!facility.featureFlags?.[
      FacilityFeatureFlagsEnum.FACILITY_ADMIN_ONLY_DELETE_ADMISSIONS
    ],
});

export type Facility = SimpleFacilityModel;

export const FacilityService = {
  /**
   * Get all facilities for the authenticated user.
   * Returns facilities filtered by user's role scope (RESIDE, ORGANIZATION, or FACILITY).
   * `includeInactive` adds every inactive facility (Reside admins only),
   * `forReporting` adds the inactive facilities kept in reporting (users with reporting access).
   */
  getAll: ({
    includeInactive = false,
    forReporting = false,
  }: {includeInactive?: boolean; forReporting?: boolean} = {}): Promise<
    ApiResponse<Page<FacilityModel>>
  > =>
    http.get<Page<FacilityModel>>("/facilities", {
      ...(includeInactive ? {includeInactive: true} : {}),
      ...(forReporting ? {forReporting: true} : {}),
    }),

  /**
   * Get facilities for a specific organization.
   */
  findByOrganization: (organizationId: string) =>
    OrganizationsService.getOrganizationFacilities(organizationId),

  create: (facility: CreateFacilityModel) => http.post("/facilities", facility),

  patch: (facilityId: string, updateModel: UpdateFacilityTemplateModel) =>
    http.patch(`/facilities/${facilityId}`, updateModel),

  /**
   * Activate or deactivate a facility, and set whether an inactive facility is kept in reporting. Reside admin only.
   */
  updateStatus: (
    facilityId: string,
    status: FacilityStatusEnum.ACTIVE | FacilityStatusEnum.INACTIVE,
    includeInReporting?: boolean,
  ): Promise<ApiResponse<FacilityModel>> =>
    http.put<FacilityModel>(`/facilities/${facilityId}/status`, {
      status,
      ...(includeInReporting === undefined ? {} : {includeInReporting}),
    }),
};

export const isFacilityInactive = (facility?: {status?: FacilityStatusEnum}) =>
  facility?.status === FacilityStatusEnum.INACTIVE;
